import React from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/FormField'
import { useTestSets } from '@/hooks/useTestSets'
import type { CreateTestSetRequest } from '@/services/evaluationService'

const createTestSetSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  systemPrompt: z.string().min(1, 'System prompt is required'),
  model: z.string().default('gpt-4o'),
  temperature: z.number().min(0).max(2).default(0.1)
})

interface CreateTestSetModalProps {
  isOpen: boolean
  onClose: () => void
}

export const CreateTestSetModal: React.FC<CreateTestSetModalProps> = ({
  isOpen,
  onClose
}) => {
  const { createTestSet } = useTestSets()

  const methods = useForm<CreateTestSetRequest>({
    resolver: zodResolver(createTestSetSchema),
    defaultValues: {
      name: '',
      description: '',
      systemPrompt: 'You are a helpful HR assistant. Answer questions based on the provided context.',
      model: 'gpt-4o',
      temperature: 0.1
    },
    mode: 'onBlur'
  })

  const { handleSubmit, reset, formState: { isSubmitting } } = methods

  const onSubmit = async (data: CreateTestSetRequest) => {
    try {
      await createTestSet(data)
      reset()
      onClose()
    } catch (err) {
      // Error handling is done in the hook
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Test Set</DialogTitle>
          <DialogDescription>
            Create a new evaluation test set to measure your RAG system performance.
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="name"
              label="Test Set Name"
              placeholder="e.g., HR Policies Basic Test"
              required
            />

            <FormField
              name="description"
              label="Description"
              placeholder="Describe what this test set evaluates..."
              type="textarea"
            />

            <FormField
              name="systemPrompt"
              label="System Prompt"
              placeholder="Enter the system prompt for the RAG responses..."
              type="textarea"
              required
              className="min-h-[120px]"
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                name="model"
                label="Model"
                type="select"
                options={[
                  { value: 'gpt-4o', label: 'GPT-4o' },
                  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
                  { value: 'gpt-4', label: 'GPT-4' }
                ]}
                required
              />

              <FormField
                name="temperature"
                label="Temperature"
                type="number"
                step="0.1"
                min="0"
                max="2"
                placeholder="0.1"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Test Set'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}